import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { AUTH_MESSAGES, mapAuthErrorMessage } from '../constants/messages';
import { getSupabaseClient, isSupabaseConfigured } from '../lib/supabase';

interface AuthActionResult {
  ok: boolean;
  message?: string;
}

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  profile: Record<string, any> | null;
  role: string;
  isAdmin: boolean;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isProfileLoading: boolean;
  isSupabaseConfigured: boolean;
  /**
   * Phase B-β Slice 14: `/user/profile` レスポンス直下の entitlements を
   * そのまま流す。EntitlementsContext がこれを優先的に読む。null は未取得 /
   * 取得失敗を意味し、その場合 EntitlementsContext は role/plan から推定する。
   */
  entitlements: Record<string, any> | null;
  errorMessage: string;
  clearError: () => void;
  refreshProfile: () => Promise<AuthActionResult>;
  signIn: (email: string, password: string) => Promise<AuthActionResult>;
  signUp: (email: string, password: string, displayName?: string) => Promise<AuthActionResult>;
  signOut: () => Promise<AuthActionResult>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Record<string, any> | null>(null);
  const [entitlements, setEntitlements] = useState<Record<string, any> | null>(null);
  const [profileRole, setProfileRole] = useState('user');
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let alive = true;
    const client = getSupabaseClient();

    if (!client) {
      setErrorMessage(AUTH_MESSAGES.unavailable);
      setIsAuthLoading(false);
      return () => {
        alive = false;
      };
    }

    const loadSession = async () => {
      try {
        const { data, error } = await client.auth.getSession();
        if (error) throw error;
        if (!alive) return;
        setSession(data.session);
        setUser(data.session?.user ?? null);
        setIsProfileLoading(Boolean(data.session?.access_token));
        setErrorMessage('');
      } catch (error) {
        if (!alive) return;
        console.error('[auth] getSession failed', error);
        setErrorMessage(mapAuthErrorMessage(error, AUTH_MESSAGES.sessionLoadFailed));
      } finally {
        if (alive) setIsAuthLoading(false);
      }
    };

    void loadSession();

    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((_event, nextSession) => {
      if (!alive) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setIsProfileLoading(Boolean(nextSession?.access_token));
      setErrorMessage('');
      setIsAuthLoading(false);
    });

    return () => {
      alive = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const loadProfile = async () => {
      if (!session?.access_token) {
        setProfile(null);
        setEntitlements(null);
        setProfileRole('user');
        setIsProfileLoading(false);
        return;
      }

      setIsProfileLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/user/profile`, {
          timeout: 10000,
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });
        if (!alive) return;
        const rawProfile = res.data.profile ?? null;
        // Slice 14: backend がトップレベル `entitlements` を返すようになった。
        // 古い backend だと undefined になるので、その場合 profile.entitlements
        // にもフォールバックする (どちらも無ければ null → EntitlementsContext で推定)。
        const ent =
          res.data.entitlements ??
          (rawProfile && (rawProfile as any).entitlements) ??
          null;
        const nextEntitlements = ent && typeof ent === 'object' ? ent : null;
        const rawRole = String(res.data.role ?? rawProfile?.role ?? 'user').trim().toLowerCase();
        const entitlementPlan = String(nextEntitlements?.plan ?? '').trim().toLowerCase();
        const nextRole = rawRole === 'admin' || entitlementPlan === 'admin' ? 'admin' : rawRole || 'user';
        const nextProfile =
          rawProfile && typeof rawProfile === 'object'
            ? { ...rawProfile, role: nextRole }
            : { role: nextRole };
        setProfile(nextProfile);
        setProfileRole(nextRole);
        setEntitlements(nextEntitlements);
      } catch (error) {
        if (!alive) return;
        console.error('[auth] loadProfile failed');
        setProfile(null);
        setEntitlements(null);
        setProfileRole('user');
      } finally {
        if (alive) setIsProfileLoading(false);
      }
    };

    void loadProfile();

    return () => {
      alive = false;
    };
  }, [session?.access_token]);

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  const refreshProfile = useCallback(async (): Promise<AuthActionResult> => {
    if (!session?.access_token) {
      setProfile(null);
      setEntitlements(null);
      setProfileRole('user');
      setIsProfileLoading(false);
      return { ok: false, message: 'auth_required' };
    }

    setIsProfileLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/user/profile`, {
        timeout: 10000,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      const rawProfile = res.data.profile ?? null;
      const ent =
        res.data.entitlements ??
        (rawProfile && (rawProfile as any).entitlements) ??
        null;
      const nextEntitlements = ent && typeof ent === 'object' ? ent : null;
      const rawRole = String(res.data.role ?? rawProfile?.role ?? 'user').trim().toLowerCase();
      const entitlementPlan = String(nextEntitlements?.plan ?? '').trim().toLowerCase();
      const nextRole = rawRole === 'admin' || entitlementPlan === 'admin' ? 'admin' : rawRole || 'user';
      const nextProfile =
        rawProfile && typeof rawProfile === 'object'
          ? { ...rawProfile, role: nextRole }
          : { role: nextRole };
      setProfile(nextProfile);
      setProfileRole(nextRole);
      setEntitlements(nextEntitlements);
      return { ok: true };
    } catch {
      console.error('[auth] refreshProfile failed');
      return { ok: false, message: AUTH_MESSAGES.sessionLoadFailed };
    } finally {
      setIsProfileLoading(false);
    }
  }, [session?.access_token]);

  const signIn = useCallback(async (email: string, password: string): Promise<AuthActionResult> => {
    const client = getSupabaseClient();
    if (!client) {
      const message = AUTH_MESSAGES.unavailable;
      setErrorMessage(message);
      return { ok: false, message };
    }

    setErrorMessage('');

    try {
      const { error } = await client.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return { ok: true };
    } catch (error) {
      console.error('[auth] signIn failed', error);
      const message = mapAuthErrorMessage(error, AUTH_MESSAGES.signInFailed);
      setErrorMessage(message);
      return { ok: false, message };
    }
  }, []);

  const signUp = useCallback(async (
    email: string,
    password: string,
    displayName?: string,
  ): Promise<AuthActionResult> => {
    const client = getSupabaseClient();
    if (!client) {
      const message = AUTH_MESSAGES.unavailable;
      setErrorMessage(message);
      return { ok: false, message };
    }

    setErrorMessage('');

    try {
      // Why: emailRedirectTo を現在のオリジンに動的にバインドする。Supabase Dashboard
      //      の Site URL がデフォルトの localhost のままだと、確認メールリンクが
      //      localhost:3000 を指してしまい本番環境で開けなくなる。明示すれば、
      //      開発時は localhost、本番時は pages.dev に正しく解決される。
      //      なお、Supabase Dashboard の Authentication → URL Configuration → Redirect URLs
      //      に当該オリジンを許可リスト登録しておく必要がある。
      const { data, error } = await client.auth.signUp({
        email,
        password,
        options: {
          data: {
            display_name: displayName?.trim() || '',
          },
          emailRedirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      });
      if (error) throw error;

      return {
        ok: true,
        message: data.session ? undefined : AUTH_MESSAGES.signUpSuccess,
      };
    } catch (error) {
      console.error('[auth] signUp failed', error);
      const message = mapAuthErrorMessage(error, AUTH_MESSAGES.signUpFailed);
      setErrorMessage(message);
      return { ok: false, message };
    }
  }, []);

  const signOut = useCallback(async (): Promise<AuthActionResult> => {
    const client = getSupabaseClient();
    if (!client) {
      const message = AUTH_MESSAGES.unavailable;
      setErrorMessage(message);
      return { ok: false, message };
    }

    setErrorMessage('');

    try {
      const { error } = await client.auth.signOut();
      if (error) throw error;
      return { ok: true };
    } catch (error) {
      console.error('[auth] signOut failed', error);
      const message = mapAuthErrorMessage(error, AUTH_MESSAGES.signOutFailed);
      setErrorMessage(message);
      return { ok: false, message };
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    session,
    user,
    profile,
    role: profileRole,
    isAdmin:
      profileRole === 'admin' ||
      String(entitlements?.plan ?? '').trim().toLowerCase() === 'admin',
    isAuthenticated: Boolean(user),
    isAuthLoading,
    isProfileLoading,
    isSupabaseConfigured,
    entitlements,
    errorMessage,
    clearError,
    refreshProfile,
    signIn,
    signUp,
    signOut,
  }), [clearError, entitlements, errorMessage, isAuthLoading, isProfileLoading, profile, profileRole, refreshProfile, session, signIn, signOut, signUp, user]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
